// src/hooks/useOffboarding.js
// ============================================================
// Offboarding & Exit Management Hooks & Helpers
// EMS Hierarchy Workflow:
//   Submit → Pending HR Review → Pending Admin Approval
//   → Notice Period Active → Handover In Progress
//   → Asset Return Verification → Clearance In Progress
//   → Admin Final Clearance → Offboarded
//
// Firestore collections:
//   "resignations"       — active resignation records
//   "offboardingHistory" — immutable audit log
// ============================================================

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config.js";

// ── Workflow Status Sequence ──────────────────────────────────
export const OFFBOARDING_STATUSES = [
  "Pending HR Review",
  "Pending Admin Approval",
  "Notice Period Active",
  "Handover In Progress",
  "Asset Return Verification",
  "Clearance In Progress",
  "Admin Final Clearance",
  "Offboarded",
];

export const OFFBOARDING_TERMINAL_STATUSES = ["Offboarded", "Rejected", "Cancelled"];

// ── Audit log writer ──────────────────────────────────────────
async function writeOffboardingHistory({
  resignationId,
  employeeName,
  action,
  performedBy,
  performedByName,
  performedByRole,
  details = "",
}) {
  try {
    await addDoc(collection(db, "offboardingHistory"), {
      resignationId,
      employeeName,
      action,
      performedBy: performedBy || "system",
      performedByName: performedByName || "System",
      performedByRole: performedByRole || "admin",
      details,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error("[writeOffboardingHistory]", err);
  }
}

// ── HOOKS ────────────────────────────────────────────────────

/**
 * useResignations — real-time listener for all resignation requests.
 */
export function useResignations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "resignations"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useResignations]", err.code);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useMyResignation — real-time listener for a single employee's resignations.
 */
export function useMyResignation(userUid) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userUid) {
      setData([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "resignations"),
      where("userUid", "==", userUid),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useMyResignation]", err.code);
        setLoading(false);
      }
    );
    return unsub;
  }, [userUid]);

  return { data, loading };
}

/**
 * useOffboardingHistory — audit trail for all offboarding events.
 */
export function useOffboardingHistory(resignationId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q;
    if (resignationId) {
      q = query(
        collection(db, "offboardingHistory"),
        where("resignationId", "==", resignationId),
        orderBy("timestamp", "desc")
      );
    } else {
      q = query(
        collection(db, "offboardingHistory"),
        orderBy("timestamp", "desc")
      );
    }
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useOffboardingHistory]", err.code);
        setLoading(false);
      }
    );
    return unsub;
  }, [resignationId]);

  return { data, loading };
}

// ── WRITE HELPERS ────────────────────────────────────────────

/**
 * submitResignation — Any user (Employee / Lead / HR / Admin) submits resignation.
 * Initial status: "Pending HR Review"
 */
export async function submitResignation(
  {
    resignationDate,
    proposedLWD,
    noticePeriodDays = 30,
    reason,
    handoverNotes = "",
  },
  userProfile
) {
  if (!userProfile?.uid) throw new Error("User profile is required.");

  const defaultClearance = { status: "Pending", approvedBy: null, approvedAt: null, comments: "" };

  const payload = {
    userId: userProfile.uid,
    userUid: userProfile.uid,
    employeeId: userProfile.employeeId || "",
    employeeName: userProfile.displayName || userProfile.name || "Employee",
    department: userProfile.department || "General",
    designation: userProfile.designation || userProfile.role || "Team Member",
    resignationDate: resignationDate || new Date().toISOString().split("T")[0],
    proposedLWD,
    officialLWD: proposedLWD,
    noticePeriodDays: Number(noticePeriodDays) || 30,
    reason,
    handoverNotes,
    status: "Pending HR Review",
    clearances: {
      hr:       { ...defaultClearance },
      teamLead: { ...defaultClearance },
      it:       { ...defaultClearance },
      finance:  { ...defaultClearance },
      admin:    { ...defaultClearance },
    },
    submittedByRole: userProfile.role || "employee",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, "resignations"), payload);

  await writeOffboardingHistory({
    resignationId: docRef.id,
    employeeName: payload.employeeName,
    action: "Resignation Submitted",
    performedBy: userProfile.uid,
    performedByName: payload.employeeName,
    performedByRole: userProfile.role || "employee",
    details: `Proposed LWD: ${proposedLWD}. Notice: ${noticePeriodDays} days. Reason: ${reason}`,
  });

  return docRef;
}

/**
 * hrApproveResignation — HR reviews and forwards to Admin for final approval.
 * Status: "Pending HR Review" → "Pending Admin Approval"
 */
export async function hrApproveResignation(resignationId, comments = "", userProfile = null) {
  const current = await _fetchResignation(resignationId);
  if (current.status !== "Pending HR Review") {
    throw new Error("Resignation is not in 'Pending HR Review' status.");
  }

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Pending Admin Approval",
    hrReviewedBy: userProfile?.displayName || "HR",
    hrReviewedAt: new Date().toISOString(),
    hrComments: comments,
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "HR Approved — Sent to Admin",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "HR",
    performedByRole: "hr",
    details: comments || "HR reviewed and forwarded to Admin for final approval.",
  });
}

/**
 * adminApproveResignation — Admin gives final approval, starts Notice Period.
 * Status: "Pending Admin Approval" → "Notice Period Active"
 */
export async function adminApproveResignation(resignationId, officialLWD = null, comments = "", userProfile = null) {
  const current = await _fetchResignation(resignationId);
  if (current.status !== "Pending Admin Approval") {
    throw new Error("Resignation is not in 'Pending Admin Approval' status.");
  }

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Notice Period Active",
    officialLWD: officialLWD || current.proposedLWD,
    adminApprovedBy: userProfile?.displayName || "Admin",
    adminApprovedAt: new Date().toISOString(),
    adminComments: comments,
    noticePeriodStartedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "Admin Approved — Notice Period Started",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "Admin",
    performedByRole: "admin",
    details: `Official LWD set to ${officialLWD || current.proposedLWD}. ${comments}`,
  });
}

/**
 * rejectResignation — HR or Admin rejects the resignation.
 * Status: → "Rejected"
 */
export async function rejectResignation(resignationId, reason = "", userProfile = null) {
  const current = await _fetchResignation(resignationId);

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Rejected",
    rejectedBy: userProfile?.displayName || "Admin/HR",
    rejectedAt: new Date().toISOString(),
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "Resignation Rejected",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "Admin/HR",
    performedByRole: userProfile?.role || "admin",
    details: reason || "Rejected by management.",
  });
}

/**
 * advanceToHandover — Move from Notice Period to Handover In Progress.
 * Status: "Notice Period Active" → "Handover In Progress"
 */
export async function advanceToHandover(resignationId, userProfile = null) {
  const current = await _fetchResignation(resignationId);

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Handover In Progress",
    handoverStartedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "Handover In Progress",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "HR/Admin",
    performedByRole: userProfile?.role || "hr",
    details: "Task & knowledge handover initiated.",
  });
}

/**
 * advanceToAssetReturn — Move to Asset Return Verification step.
 * Status: "Handover In Progress" → "Asset Return Verification"
 */
export async function advanceToAssetReturn(resignationId, userProfile = null) {
  const current = await _fetchResignation(resignationId);

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Asset Return Verification",
    assetReturnStartedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "Asset Return Verification Started",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "HR/Admin",
    performedByRole: userProfile?.role || "hr",
    details: "Asset return verification phase initiated.",
  });
}

/**
 * startClearance — Begin department clearance phase.
 * Status: "Asset Return Verification" / "Notice Period Active" → "Clearance In Progress"
 */
export async function startClearance(resignationId, userProfile = null) {
  const current = await _fetchResignation(resignationId);

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Clearance In Progress",
    clearanceStartedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "Department Clearances Started",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "HR/Admin",
    performedByRole: userProfile?.role || "hr",
    details: "All department clearances initiated.",
  });
}

/**
 * updateResignationStatus — Generic status override.
 */
export async function updateResignationStatus(
  resignationId,
  status,
  additionalData = {},
  userProfile = null
) {
  const current = await _fetchResignation(resignationId);

  await updateDoc(doc(db, "resignations", resignationId), {
    status,
    ...additionalData,
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName || "Employee",
    action: `Status Updated to ${status}`,
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || userProfile?.name || "Admin/HR",
    performedByRole: userProfile?.role || "admin",
    details: additionalData.comments || `Status changed from ${current.status} to ${status}`,
  });
}

/**
 * updateDepartmentClearance — Approve/Reject a specific department clearance.
 * If all 5 clearances approved → moves to "Admin Final Clearance".
 */
export async function updateDepartmentClearance(
  resignationId,
  deptKey, // "hr" | "teamLead" | "it" | "finance" | "admin"
  { status, comments = "" },
  userProfile = null
) {
  const current = await _fetchResignation(resignationId);
  const currentClearances = current.clearances || {};

  const updatedClearances = {
    ...currentClearances,
    [deptKey]: {
      status,
      approvedBy: userProfile?.displayName || userProfile?.name || "Reviewer",
      approvedAt: new Date().toISOString(),
      comments,
    },
  };

  const allApproved = Object.values(updatedClearances).every((c) => c.status === "Approved");
  let newOverallStatus = current.status;
  if (allApproved && current.status === "Clearance In Progress") {
    newOverallStatus = "Admin Final Clearance";
  }

  await updateDoc(doc(db, "resignations", resignationId), {
    clearances: updatedClearances,
    status: newOverallStatus,
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName || "Employee",
    action: `${deptKey.toUpperCase()} Clearance: ${status}`,
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || userProfile?.name || "Reviewer",
    performedByRole: userProfile?.role || "hr",
    details: `${deptKey.toUpperCase()} clearance marked ${status}. ${comments ? `Comments: ${comments}` : ""}${allApproved ? " — All clearances complete, awaiting Admin final clearance." : ""}`,
  });
}

/**
 * adminFinalClearance — Admin gives final sign-off, employee marked Offboarded.
 * Status: "Admin Final Clearance" → "Offboarded"
 */
export async function adminFinalClearance(resignationId, comments = "", userProfile = null) {
  const current = await _fetchResignation(resignationId);
  if (current.status !== "Admin Final Clearance") {
    throw new Error("All department clearances must be approved before Admin final clearance.");
  }

  await updateDoc(doc(db, "resignations", resignationId), {
    status: "Offboarded",
    offboardedAt: new Date().toISOString(),
    adminFinalClearanceBy: userProfile?.displayName || "Admin",
    adminFinalClearanceAt: new Date().toISOString(),
    adminFinalComments: comments,
    updatedAt: serverTimestamp(),
  });

  await writeOffboardingHistory({
    resignationId,
    employeeName: current.employeeName,
    action: "Admin Final Clearance — Employee Offboarded",
    performedBy: userProfile?.uid,
    performedByName: userProfile?.displayName || "Admin",
    performedByRole: "admin",
    details: comments || "Admin granted final clearance. Employee successfully offboarded.",
  });
}

// ── Private helper ────────────────────────────────────────────
async function _fetchResignation(resignationId) {
  const snap = await getDocs(
    query(collection(db, "resignations"), where("__name__", "==", resignationId))
  );
  return snap.docs[0]?.data() || {};
}
